import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { resolveSemantic } from '../tokens/resolve';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

/** localStorage key; also read by the inline pre-paint script in apps/web/index.html. */
export const THEME_STORAGE_KEY = 'gg-theme';

const DARK_QUERY = '(prefers-color-scheme: dark)';

function readPreference(): ThemePreference {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

function systemTheme(): ResolvedTheme {
  return typeof matchMedia === 'function' && matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
}

function resolve(pref: ThemePreference): ResolvedTheme {
  return pref === 'system' ? systemTheme() : pref;
}

/** Sets `data-theme` on <html> (absent = follow system) and the mobile browser chrome color. */
export function applyTheme(pref: ThemePreference): void {
  const root = document.documentElement;
  if (pref === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', pref);

  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'theme-color';
    document.head.appendChild(meta);
  }
  meta.content = resolveSemantic('bg.canvas', resolve(pref));
}

function subscribeSystem(onChange: () => void): () => void {
  if (typeof matchMedia !== 'function') return () => {};
  const mq = matchMedia(DARK_QUERY);
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}

/** Theme preference (system by default), persisted per device. */
export function useTheme() {
  const [preference, setPreferenceState] = useState<ThemePreference>(readPreference);
  const system = useSyncExternalStore(subscribeSystem, systemTheme, () => 'light' as const);
  const resolved: ResolvedTheme = preference === 'system' ? system : preference;

  // Sync the DOM (an external system) with the current preference / system theme.
  useEffect(() => {
    applyTheme(preference);
  }, [preference, system]);

  const setPreference = useCallback((pref: ThemePreference) => {
    try {
      if (pref === 'system') localStorage.removeItem(THEME_STORAGE_KEY);
      else localStorage.setItem(THEME_STORAGE_KEY, pref);
    } catch {
      // Private mode etc.: the choice just won't persist.
    }
    setPreferenceState(pref);
  }, []);

  return { preference, resolved, setPreference };
}
